/**
 * 首页待办本地快照（仅读本地，不打平台 OpenAPI）。
 * 议价单 / 建议零售价在各自模块内同步平台后回写；首页只读这份快照。
 */
const STORAGE_PREFIX = 'ipe_shein_home_local_todo_';

function storageKey(shopId) {
  return `${STORAGE_PREFIX}${String(shopId || '').trim()}`;
}

function emptySnapshot() {
  return {
    discussPending: null,
    rrpPendingAudit: null,
    rrpAuditFailed: null,
    updatedAt: '',
  };
}

export function readHomeLocalTodoSnapshot(shopId) {
  if (!shopId || typeof localStorage === 'undefined') {
    return emptySnapshot();
  }
  try {
    const raw = localStorage.getItem(storageKey(shopId));
    if (!raw) return emptySnapshot();
    const parsed = JSON.parse(raw);
    return {
      discussPending: parsed?.discussPending == null ? null : Number(parsed.discussPending) || 0,
      rrpPendingAudit: parsed?.rrpPendingAudit == null ? null : Number(parsed.rrpPendingAudit) || 0,
      rrpAuditFailed: parsed?.rrpAuditFailed == null ? null : Number(parsed.rrpAuditFailed) || 0,
      updatedAt: String(parsed?.updatedAt || ''),
    };
  } catch {
    return emptySnapshot();
  }
}

export function writeHomeLocalTodoSnapshot(shopId, patch = {}) {
  if (!shopId || typeof localStorage === 'undefined') return emptySnapshot();
  const prev = readHomeLocalTodoSnapshot(shopId);
  const next = {
    ...prev,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  if (Object.prototype.hasOwnProperty.call(patch, 'discussPending')) {
    next.discussPending = patch.discussPending == null ? null : Number(patch.discussPending) || 0;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'rrpPendingAudit')) {
    next.rrpPendingAudit = patch.rrpPendingAudit == null ? null : Number(patch.rrpPendingAudit) || 0;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'rrpAuditFailed')) {
    next.rrpAuditFailed = patch.rrpAuditFailed == null ? null : Number(patch.rrpAuditFailed) || 0;
  }
  try {
    localStorage.setItem(storageKey(shopId), JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('ipe-shein-home-local-todo', {
      detail: { shopId: String(shopId), snapshot: next },
    }));
  }
  return next;
}

/** 议价单模块：同步到「待商家确认」列表后回写首页快照 */
export function rememberDiscussPendingForHome(shopId, pendingCount) {
  return writeHomeLocalTodoSnapshot(shopId, {
    discussPending: Number(pendingCount) || 0,
  });
}

/** 建议零售价模块：按审核态回写首页快照（兼容旧调用） */
export function rememberRrpAuditForHome(shopId, { auditState, total } = {}) {
  const n = Number(total) || 0;
  const state = String(auditState ?? '');
  if (state === '1') {
    return writeHomeLocalTodoSnapshot(shopId, { rrpPendingAudit: n });
  }
  if (state === '3') {
    return writeHomeLocalTodoSnapshot(shopId, { rrpAuditFailed: n });
  }
  return readHomeLocalTodoSnapshot(shopId);
}

/** 建议零售价当前价：用 pending-count 回写首页快照 */
export function rememberRrpPendingForHome(shopId, counts = {}) {
  const reviewing = counts.reviewing == null ? null : Number(counts.reviewing) || 0;
  const sampleFail = Number(counts.sampleFail) || 0;
  const auditFail = Number(counts.auditFail) || 0;
  const failed = counts.auditFail == null && counts.sampleFail == null
    ? (counts.rrpAuditFailed == null ? null : Number(counts.rrpAuditFailed) || 0)
    : sampleFail + auditFail;
  return writeHomeLocalTodoSnapshot(shopId, {
    rrpPendingAudit: reviewing,
    rrpAuditFailed: failed,
  });
}

/** 合并多店本地快照（全部店铺首页） */
export function readHomeLocalTodoSnapshotsAggregate(shopIds = []) {
  const ids = (Array.isArray(shopIds) ? shopIds : [])
    .map((id) => String(id || '').trim())
    .filter(Boolean);
  if (!ids.length) return emptySnapshot();
  let discussPending = null;
  let rrpPendingAudit = null;
  let rrpAuditFailed = null;
  let updatedAt = '';
  let updatedTs = 0;
  for (const id of ids) {
    const snap = readHomeLocalTodoSnapshot(id);
    if (snap.discussPending != null) {
      discussPending = (discussPending || 0) + Number(snap.discussPending || 0);
    }
    if (snap.rrpPendingAudit != null) {
      rrpPendingAudit = (rrpPendingAudit || 0) + Number(snap.rrpPendingAudit || 0);
    }
    if (snap.rrpAuditFailed != null) {
      rrpAuditFailed = (rrpAuditFailed || 0) + Number(snap.rrpAuditFailed || 0);
    }
    const ts = snap.updatedAt ? new Date(snap.updatedAt).getTime() : 0;
    if (!Number.isNaN(ts) && ts > updatedTs) {
      updatedTs = ts;
      updatedAt = snap.updatedAt;
    }
  }
  return { discussPending, rrpPendingAudit, rrpAuditFailed, updatedAt };
}
